import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FieldArray, useFormikContext } from 'formik';
import { unwrapResult } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { AddOutlined } from '@mui/icons-material';

import { CustomButton, SearchSelect, Text } from '../../../shared/uiComponents';
import { dateNormalizer, errorNormalizer } from '../../../shared/Helpers/functions';
import { PRIMARY, TEXT } from '../../../shared/Helpers/colors';
import { DispatchProperties } from '../../../redux/store';
import { getTherapistsDetails } from '../../../redux/State/clientSlice/userClientSlice';
import { RecurringSchedule, RecurringScheduleViewModel } from '../../../redux/API/ClientAPIHelpers/recurringSchedule';
import { ScheduleCancellation, ScheduleCancellationViewModel, CancellationStatus, CancellationType } from '../../../redux/API/ClientAPIHelpers/scheduleCancellation';
import { ActiveAuthorizationProperties } from '../../../redux/API/ClientAPIHelpers/insuranceAuthorizationProperties';
import { SessionTypeProperties } from '../../../redux/API/ClientAPIHelpers/calendarProperties';
import { TherapistDetailsProperties } from '../../../redux/API/ClientAPIHelpers/clientProperties';

import AiPromptBox from './AiPromptBox';
import ScheduleBlock from './ScheduleBlock';
import { AdminTypes } from '../../../components/Menu';
import { classifyAuthorizations, clearClientBoundFields, computeDateDefaultsForPeriod, emptyScheduleBlock, formatMinutesToTime, formatProviderLabel, pickDefaultAuthPeriod, resolveAiSchedules, resolveBlockHints } from './helpers';
import {
	AiParsedForm,
	AuthDetailMetric,
	AuthorizationBreakdownResponse,
	AuthorizationPeriod,
	AuthorizationPeriodsBySessionType,
	AuthorizationSummary,
	BcbaAllocationSummary,
	AuthPeriodOption,
	ClassifiedAuthorizations,
	ProviderOption,
	ScheduleFormValues,
	SessionTypeOption,
} from './types';
import { AuthBounds } from './validation';
import { useClientSearch } from '../useClientSearch';

interface ScheduleFormProperties {
	onAuthBoundsChange?: (bounds: AuthBounds) => void;
	onClose: () => void;
}

const ScheduleForm: FC<ScheduleFormProperties> = ({ onAuthBoundsChange, onClose }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const { values, setValues, setFieldValue, submitForm, isSubmitting } = useFormikContext<ScheduleFormValues>();

	const { clientSearch, setClientSearch, clientOptions, clientHasMore, setClientPageSize } = useClientSearch(false, true);
	const [authorizations, setAuthorizations] = useState<ActiveAuthorizationProperties[]>([]);
	const [bcbaAllocation, setBcbaAllocation] = useState<BcbaAllocationSummary | null>(null);
	const [therapists, setTherapists] = useState<TherapistDetailsProperties[]>([]);
	const [loadingClientData, setLoadingClientData] = useState(false);

	const [existingSchedules, setExistingSchedules] = useState<RecurringScheduleViewModel[]>([]);
	const [sessionTypesByProvider, setSessionTypesByProvider] = useState<Record<string, SessionTypeOption[]>>({});
	const [cancellationsByProvider, setCancellationsByProvider] = useState<Record<string, ScheduleCancellationViewModel[]>>({});

	const fetchedSessionTypeProviders = useRef<Set<string>>(new Set());
	const fetchedCancellationProviders = useRef<Set<string>>(new Set());

	const dateDefaultedUids = useRef<Set<string>>(new Set());
	const resolvedHintUids = useRef<Set<string>>(new Set());

	const fetchGeneration = useRef(0);

	useEffect(() => {
		fetchGeneration.current += 1;
		const generation = fetchGeneration.current;

		if (!values.clientId) {
			setAuthorizations([]);
			setBcbaAllocation(null);
			setTherapists([]);
			setExistingSchedules([]);
			setSessionTypesByProvider({});
			setCancellationsByProvider({});
			setLoadingClientData(false);
			fetchedSessionTypeProviders.current.clear();
			fetchedCancellationProviders.current.clear();
			dateDefaultedUids.current.clear();
			resolvedHintUids.current.clear();

			return;
		}

		setLoadingClientData(true);
		setSessionTypesByProvider({});
		fetchedSessionTypeProviders.current.clear();
		fetchedCancellationProviders.current.clear();
		dateDefaultedUids.current.clear();
		resolvedHintUids.current.clear();

		let pending = 2;

		const maybeFinishLoading = () => {
			pending -= 1;

			if (pending === 0) setLoadingClientData(false);
		};

		// Sourced from the schedules-scoped endpoint (Client.Read + schedules role gate) rather than
		// the insurance-authorization endpoint, so providers without Client.Authorization.Read (e.g.
		// BCBAs) can open this modal. Same payload as the original active-authorizations call.
		RecurringSchedule.getActiveAuthorizations(values.clientId)
			.then((response) => {
				if (fetchGeneration.current !== generation || response.error) return;
				setAuthorizations((response as ActiveAuthorizationProperties[]) || []);
			})
			.catch(errorNormalizer)
			.finally(maybeFinishLoading);

		dispatch(getTherapistsDetails({ clientId: values.clientId }))
			.then(unwrapResult)
			.then((response) => {
				if (fetchGeneration.current !== generation) return;
				setTherapists((response as TherapistDetailsProperties[]) || []);
			})
			.catch(errorNormalizer)
			.finally(maybeFinishLoading);

		RecurringSchedule.getAuthorizationBreakdown(values.clientId)
			.then((response) => {
				if (fetchGeneration.current !== generation || response.error) return;
				const data = response as AuthorizationBreakdownResponse;
				setBcbaAllocation(data.bcbaAllocation || null);
			})
			.catch(errorNormalizer);

		RecurringSchedule.getByClient(values.clientId)
			.then((response) => {
				if (fetchGeneration.current !== generation || response.error) return;
				const today = dayjs().format('YYYY-MM-DD');
				const active = ((response as RecurringScheduleViewModel[]) || []).filter(
					(s) => !s.endDate || dayjs(s.endDate).format('YYYY-MM-DD') >= today,
				);
				setExistingSchedules(active);
			})
			.catch(errorNormalizer);
	}, [dispatch, values.clientId]);

	const providers = useMemo<ProviderOption[]>(
		() =>
			therapists.map((therapist) => ({
				id: therapist.id,
				label: formatProviderLabel(therapist.fullName, therapist.role.section.name),
				roleSectionId: therapist.role.section.id,
				roleSectionName: therapist.role.section.name,
			})),
		[therapists],
	);

	// The selected provider's id is passed to the schedules-scoped session-types endpoint (with
	// clientId + date), which applies the system's identical role + Meet & Greet allocation filtering
	// as session creation. Fetched per chosen provider and cached by provider id.
	const chosenProviderIds = useMemo(
		() => Array.from(new Set(values.schedules.map((block) => block.providerId).filter(Boolean))),
		[values.schedules],
	);

	useEffect(() => {
		if (!values.clientId || !chosenProviderIds.length) return;

		fetchedSessionTypeProviders.current.clear();
		const missing = chosenProviderIds.filter((id) => !fetchedSessionTypeProviders.current.has(id));
		if (!missing.length) return;

		missing.forEach((id) => fetchedSessionTypeProviders.current.add(id));

		const date = dateNormalizer(dayjs());
		let cancelled = false;

		Promise.all(
			missing.map((providerId) =>
				RecurringSchedule.getProviderSessionTypes({ clientId: values.clientId, providerId, date })
					.then((response) => ({
						providerId,
						types: response.error ? [] : ((response as SessionTypeProperties[]) || []),
					}))
					.catch(() => ({ providerId, types: [] as SessionTypeProperties[] })),
			),
		).then((results) => {
			if (cancelled) return;

			setSessionTypesByProvider((previous) => {
				const next = { ...previous };
				results.forEach(({ providerId, types }) => {
					next[providerId] = types
						.filter((type) => {
							if (type.id === 0 || !type.funderId) return type.id !== 0;

							// Funder-scoped session types that participate in authorization (DOE)
							// must have a matching authorization with allocated hours. Funders that
							// don't participate in authorization at all (ELC — RequiresAuthorization
							// = false, no authorization entries) are let through unconditionally;
							// the backend already bypasses auth gating for them.
							const funderHasAnyAuth = authorizations.some(
								(a) => a.funderId === type.funderId,
							);
							if (!funderHasAnyAuth) return true;

							return authorizations.some((authorization) =>
								authorization.funderId === type.funderId &&
								authorization.authorizationDetails.some(
									(detail) => detail.sessionTypeId === type.id && detail.hoursPerWeek > 0,
								),
							);
						})
						.map((type) => ({
							id: type.id,
							label: type.name,
							type: type.type,
							isForRbts: type.allowedRoleSections?.some((rs) => rs.id === AdminTypes.bt) ?? false,
						}));
				});

				return next;
			});
		});

		return () => {
			cancelled = true;
		};
	}, [values.clientId, chosenProviderIds, authorizations]);

	// Fetch approved/pending cancellations for each chosen provider so the UI can warn
	// when a new schedule overlaps existing cancellations. Cached per provider id.
	useEffect(() => {
		if (!values.clientId || !chosenProviderIds.length) return;

		const missing = chosenProviderIds.filter((id) => !fetchedCancellationProviders.current.has(`${values.clientId}:${id}`));
		if (!missing.length) return;

		missing.forEach((id) => fetchedCancellationProviders.current.add(`${values.clientId}:${id}`));

		let cancelled = false;

		Promise.all(
			missing.map((providerId) =>
				ScheduleCancellation.getCancellations({ clientId: values.clientId, providerId })
					.then((response) => ({
						providerId,
						cancellations: (response.error ? [] : ((response as ScheduleCancellationViewModel[]) || []))
							.filter(
								(c) =>
									c.type === CancellationType.ExtendedTimeOff &&
									c.status !== CancellationStatus.Denied,
							),
					}))
					.catch(() => ({ providerId, cancellations: [] as ScheduleCancellationViewModel[] })),
			),
		).then((results) => {
			if (cancelled) return;

			setCancellationsByProvider((previous) => {
				const next = { ...previous };
				results.forEach(({ providerId, cancellations }) => {
					next[providerId] = cancellations;
				});

				return next;
			});
		});

		return () => {
			cancelled = true;
		};
	}, [values.clientId, chosenProviderIds]);

	// Classify authorizations into current (covers today) vs future (starts after today).
	const classifiedAuths = useMemo<ClassifiedAuthorizations>(
		() => classifyAuthorizations(authorizations),
		[authorizations],
	);

	// Selected auth period determines date bounds for schedule blocks.
	const [selectedAuthId, setSelectedAuthId] = useState<string | null>(null);

	// Auto-select the best default auth period when authorizations load.
	useEffect(() => {
		const defaultPeriod = pickDefaultAuthPeriod(classifiedAuths);
		setSelectedAuthId(defaultPeriod?.id ?? null);
	}, [classifiedAuths]);

	const selectedAuthPeriod = useMemo<AuthPeriodOption | null>(() => {
		if (!selectedAuthId) return null;
		const all = [...classifiedAuths.current, ...classifiedAuths.future];
		return all.find((a) => a.id === selectedAuthId) ?? null;
	}, [selectedAuthId, classifiedAuths]);

	const authPeriodOptions = useMemo(() => {
		return [...classifiedAuths.current, ...classifiedAuths.future];
	}, [classifiedAuths]);

	const hasMultipleAuthPeriods = authPeriodOptions.length > 1;

	// Derive detail metrics directly from the selected authorization's inline scheduleMetrics.
	// No separate API call — everything came in the initial getActiveAuthorizations response.
	const detailMetrics = useMemo(
		() => {
			if (!selectedAuthId) return [];
			const auth = authorizations.find((a) => a.id === selectedAuthId);
			return auth?.scheduleMetrics ?? [];
		},
		[selectedAuthId, authorizations],
	);

	// Determine which role bucket to show based on ALL chosen providers (not just the first).
	// Only filter when every chosen provider is the same role type; otherwise show all.
	const showRbtHours = useMemo<boolean | null>(() => {
		const providerIds = values.schedules.map((b) => b.providerId).filter(Boolean);
		if (!providerIds.length) return null; // no providers yet → show all

		const roles = new Set(
			providerIds.map((id) => providers.find((p) => p.id === id)?.roleSectionId).filter((r) => r !== undefined),
		);

		if (roles.size !== 1) return null; // mixed roles → show all

		const singleRole = Array.from(roles)[0];
		return singleRole !== AdminTypes.bcba && singleRole !== AdminTypes.cs;
	}, [values.schedules, providers]);

	// Filter detail metrics by role when all providers share the same role. Show all when mixed/none.
	const filteredDetailMetrics = useMemo(
		() => showRbtHours === null
			? detailMetrics
			: detailMetrics.filter((d) => (showRbtHours ? d.isForRbts : !d.isForRbts)),
		[detailMetrics, showRbtHours],
	);

	// Derive summary numbers from the selected authorization's metrics and details.
	// The selector therefore changes both the displayed hours and the displayed period.
	const authorizationSummary = useMemo<AuthorizationSummary | null>(() => {
		if (!authorizations.length || !selectedAuthPeriod) return null;

		const selectedAuthorization = authorizations.find((authorization) => authorization.id === selectedAuthPeriod.id);
		if (!selectedAuthorization) return null;

		const doeSessionTypeNames = new Set(
			selectedAuthorization.funderId
				? selectedAuthorization.authorizationDetails.map((detail) => detail.sessionType.name)
				: [],
		);
		const sumMetrics = (isDoe: boolean, metric: keyof AuthDetailMetric) => filteredDetailMetrics
			.filter((detail) => doeSessionTypeNames.has(detail.sessionTypeName) === isDoe)
			.reduce((sum, detail) => sum + Number(detail[metric] || 0), 0);
		const sumAuthorized = (isDoe: boolean) => Boolean(selectedAuthorization.funderId) === isDoe
			? selectedAuthorization.authorizationDetails.reduce((sum, detail) => sum + (detail.hoursPerWeek || 0), 0)
			: 0;
		const authHours = Math.round(sumAuthorized(false) * 100) / 100;
		const authScheduled = Math.round(sumMetrics(false, 'scheduledHoursPerWeek') * 100) / 100;
		const doeAuthHours = Math.round(sumAuthorized(true) * 100) / 100;
		const doeAuthScheduled = Math.round(sumMetrics(true, 'scheduledHoursPerWeek') * 100) / 100;

		return {
			authorizedHoursPerWeek: authHours,
			currentlyScheduledHoursPerWeek: authScheduled,
			availableHoursPerWeek: Math.max(0, Math.round((authHours - authScheduled) * 100) / 100),
			doeAuthorizedHoursPerWeek: doeAuthHours,
			doeCurrentlyScheduledHoursPerWeek: doeAuthScheduled,
			doeAvailableHoursPerWeek: Math.max(0, Math.round((doeAuthHours - doeAuthScheduled) * 100) / 100),
			expirationDate: selectedAuthPeriod.endDate,
			isDoe: Boolean(selectedAuthorization.funderId),
			startDate: selectedAuthPeriod.startDate,
		};
	}, [authorizations, filteredDetailMetrics, selectedAuthPeriod]);

	const authorizationPeriods = useMemo<AuthorizationPeriodsBySessionType>(() => authorizations.reduce<AuthorizationPeriodsBySessionType>((result, authorization) => {
		const period: AuthorizationPeriod = {
			startDate: dayjs(authorization.startDate).format('YYYY-MM-DD'),
			endDate: dayjs(authorization.endDate).format('YYYY-MM-DD'),
		};

		authorization.authorizationDetails.forEach((detail) => {
			if (!detail.sessionTypeId) return;

			const key = String(detail.sessionTypeId);
			result[key] = [...(result[key] || []), period]
				.sort((a, b) => a.startDate.localeCompare(b.startDate));
		});

		return result;
	}, {}), [authorizations]);

	const existingSchedulesByProvider = useMemo(() => {
		const SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
		const btIds = new Set(therapists.filter((t) => t.role.section.id === AdminTypes.bt).map((t) => t.id));
		const grouped = new Map<string, { providerName: string; descriptions: string[] }>();

		existingSchedules.filter((s) => btIds.has(s.providerId)).forEach((schedule) => {
			const entry = grouped.get(schedule.providerId) ?? { providerName: schedule.providerName, descriptions: [] };

			const byTime = new Map<string, number[]>();
			schedule.timeSlots.forEach((slot) => {
				const key = `${formatMinutesToTime(slot.startTimeMinutes)}–${formatMinutesToTime(slot.endTimeMinutes)}`;
				const days = byTime.get(key) ?? [];
				days.push(slot.dayOfWeek);
				byTime.set(key, days);
			});
			byTime.forEach((days, time) => {
				const dayLabels = days.sort((a, b) => a - b).map((d) => SHORT_DAYS[d]).join('/');
				entry.descriptions.push(`${dayLabels} ${time}`);
			});

			grouped.set(schedule.providerId, entry);
		});

		return grouped;
	}, [existingSchedules, therapists]);

	useEffect(() => {
		onAuthBoundsChange?.(authorizationPeriods);
	}, [authorizationPeriods, onAuthBoundsChange]);

	// When the selected auth period changes, update all blocks that haven't been manually edited.
	const prevAuthPeriodId = useRef<string | null>(null);

	useEffect(() => {
		let result = values.schedules;
		let datesChanged = false;
		let hintsChanged = false;

		const defaults = computeDateDefaultsForPeriod(selectedAuthPeriod);
		const authPeriodChanged = prevAuthPeriodId.current !== null && prevAuthPeriodId.current !== selectedAuthPeriod?.id;
		prevAuthPeriodId.current = selectedAuthPeriod?.id ?? null;

		if (defaults) {
			result = result.map((block) => {
				// If the auth period was explicitly switched, re-apply dates to all blocks.
				// Otherwise only apply to blocks that haven't been defaulted yet.
				const shouldApply = authPeriodChanged || !dateDefaultedUids.current.has(block.uid);

				if (!shouldApply) return block;

				const needsStart = authPeriodChanged || !block.startDate;
				const needsEnd = authPeriodChanged || !block.endDate;

				dateDefaultedUids.current.add(block.uid);

				if (!needsStart && !needsEnd) return block;

				datesChanged = true;

				return {
					...block,
					startDate: needsStart ? defaults.defaultStart : block.startDate,
					endDate: needsEnd ? defaults.authEnd : block.endDate,
				};
			});
		}

		if (providers.length) {
			const hasUnresolved = result.some(
				(block) => (block.providerHint || block.sessionTypeHint) && !resolvedHintUids.current.has(block.uid),
			);

			if (hasUnresolved) {
				result = result.map((block) => {
					if (!block.providerHint && !block.sessionTypeHint) return block;
					if (resolvedHintUids.current.has(block.uid)) return block;

					const resolved = resolveBlockHints(block, providers, sessionTypesByProvider[block.providerId] || []);

					if (!resolved.providerHint && !resolved.sessionTypeHint) {
						resolvedHintUids.current.add(block.uid);
					}

					if (
						resolved.providerId !== block.providerId ||
						resolved.sessionTypeId !== block.sessionTypeId ||
						resolved.providerHint !== block.providerHint ||
						resolved.sessionTypeHint !== block.sessionTypeHint
					) {
						hintsChanged = true;
					}

					return resolved;
				});
			}
		}

		if (datesChanged || hintsChanged) {
			setFieldValue('schedules', result);
		}
	}, [selectedAuthPeriod, providers, sessionTypesByProvider, values.schedules, setFieldValue]);

	const handleClientSelect = (clientId: string) => {
		if (clientId !== values.clientId) {
			setValues({ clientId, schedules: clearClientBoundFields(values.schedules) });
		}

		const selected = clientOptions.find((option) => option.id === clientId);

		if (selected) setClientSearch(selected.label);
	};

	const handleAiParsed = (parsed: AiParsedForm) => {
		const blocks = resolveAiSchedules(parsed);

		const defaults = computeDateDefaultsForPeriod(selectedAuthPeriod);

		const withDefaults = defaults
			? blocks.map((block) => ({
					...block,
					startDate: block.startDate || defaults.defaultStart,
					endDate: block.endDate || defaults.authEnd,
				}))
			: blocks;

		if (defaults) {
			withDefaults.forEach((block) => dateDefaultedUids.current.add(block.uid));
		}

		setValues({ clientId: values.clientId, schedules: withDefaults });
	};

	return (
		<div className="scheduleForm">
			<SearchSelect
				label="Select Client *"
				placeholder="Search client by name..."
				data={clientOptions}
				hasMore={clientHasMore}
				searchString={clientSearch}
				setSize={setClientPageSize}
				setSearchString={setClientSearch}
				setValue={handleClientSelect}
				description="Authorization hours and expiration will be displayed after selection"
			/>

			{loadingClientData && (
				<Text title="Loading authorization and provider data..." type="pxs" color={PRIMARY.DARK[16]} />
			)}

			{Boolean(values.clientId) && !loadingClientData && authorizations.length === 0 && (
				<div className="noAuthWarningBox">
					<Text
						title="No active authorization found for this client. An authorization (or temporary authorization) must be added before creating a schedule."
						type="pxs"
					/>
				</div>
			)}

			{authorizations.length > 0 && (
				<>
					{Boolean(authorizationSummary) && (
						<div className="authorizationInfoBox">
							<Text title="AUTHORIZATION INFO" type="pxs" fontWeight="semiBold" color={PRIMARY.DARK[16]} />

							{hasMultipleAuthPeriods && (
								<div className="authPeriodSelector">
									{authPeriodOptions.map((opt) => (
										<button
											key={opt.id}
											type="button"
											className={`authPeriodButton ${selectedAuthId === opt.id ? 'active' : ''}`}
											onClick={() => setSelectedAuthId(opt.id)}
										>
											<span className="authPeriodKind">
												{opt.kind === 'current' ? 'Current' : 'Future'}
											</span>
											<span className="authPeriodDates">
												{dayjs(opt.startDate).format('MMM D, YYYY')} – {dayjs(opt.endDate).format('MMM D, YYYY')}
											</span>
										</button>
									))}
								</div>
							)}

							<div className="authorizationInfoGrid">
								{/* Only the selected authorization's funder is shown — a schedule is created against
								    one authorization, so the other funder's metrics are not applicable here. */}
								{authorizationSummary?.isDoe ? (
									<>
										<p className="authInfoItem">
											<span className="authInfoLabel">DOE Authorized Hours/Week:</span>{' '}
											{authorizationSummary.doeAuthorizedHoursPerWeek}
										</p>
										<p className="authInfoItem">
											<span className="authInfoLabel">DOE Currently Scheduled:</span>{' '}
											{authorizationSummary.doeCurrentlyScheduledHoursPerWeek} hours
										</p>
										<p className="authInfoItem">
											<span className="authInfoLabel">DOE Available:</span>{' '}
											{authorizationSummary.doeAvailableHoursPerWeek} hours
										</p>
									</>
								) : (
									<>
										<p className="authInfoItem">
											<span className="authInfoLabel">Insurance Authorized Hours/Week:</span>{' '}
											{authorizationSummary?.authorizedHoursPerWeek}
										</p>
										<p className="authInfoItem">
											<span className="authInfoLabel">Insurance Currently Scheduled:</span>{' '}
											{authorizationSummary?.currentlyScheduledHoursPerWeek} hours
										</p>
										<p className="authInfoItem">
											<span className="authInfoLabel">Insurance Available:</span>{' '}
											{authorizationSummary?.availableHoursPerWeek} hours
										</p>
									</>
								)}
								{bcbaAllocation && (
									<>
										<p className="authInfoItem">
											<span className="authInfoLabel">BCBA Allocated Hours/Week:</span>{' '}
											{bcbaAllocation.allocatedHoursPerWeek}
										</p>
										<p className="authInfoItem">
											<span className="authInfoLabel">BCBA Scheduled:</span>{' '}
											{bcbaAllocation.scheduledHoursPerWeek} hours
										</p>
										<p className="authInfoItem">
											<span className="authInfoLabel">BCBA Available:</span>{' '}
											{bcbaAllocation.availableHoursPerWeek} hours
										</p>
									</>
								)}
								<p className="authInfoItem">
									<span className="authInfoLabel">Authorization Period:</span>{' '}
									{selectedAuthPeriod
										? `${dayjs(selectedAuthPeriod.startDate).format('MMM D, YYYY')} – ${dayjs(selectedAuthPeriod.endDate).format('MMM D, YYYY')}`
										: '—'}
								</p>
							</div>
							{detailMetrics.length > 0 && (
								<div className="authDetailChips">
									{detailMetrics.map((d) => {
										// Entire Period details are enforced as a period-total budget, so they are
										// rendered against their total hours rather than a weekly rate.
										const isWeekly = d.isWeekly !== false && d.authorizedHoursPerWeek > 0;
										const authorized = isWeekly ? d.authorizedHoursPerWeek : d.authorizedTotalHours;
										const scheduled = isWeekly ? d.scheduledHoursPerWeek : d.scheduledTotalHours;
										const pct = authorized > 0
											? Math.min(100, (scheduled / authorized) * 100)
											: 0;

										return (
											<span key={`${d.sessionTypeName}-${isWeekly ? 'wk' : 'total'}`} className="authDetailChip">
												<span className="authDetailChipLabel">
													{d.sessionTypeName.replace(/^\d+\s*-\s*/, '')}
												</span>
												<span className="authDetailChipBar">
													<span className="authDetailChipFill" style={{ width: `${pct}%` }} />
												</span>
												<span className="authDetailChipValue">
													{scheduled}/{authorized}h{isWeekly ? '/wk' : ' total'}
												</span>
											</span>
										);
									})}
								</div>
							)}
						</div>
					)}

					{existingSchedulesByProvider.size > 0 && (
						<div className="existingSchedulesBox">
							<Text title="BT CURRENT SCHEDULES" type="pxs" fontWeight="semiBold" color={TEXT.SECONDARY} />
							{Array.from(existingSchedulesByProvider.entries()).map(([providerId, { providerName, descriptions }]) => (
								<div key={providerId} className="existingScheduleProvider">
									<Text title={providerName} type="psm" fontWeight="semiBold" />
									<Text title={descriptions.join(' · ')} type="pxs" color={TEXT.TERTIARY} />
								</div>
							))}
						</div>
					)}

					{Boolean(values.clientId) && (
						<AiPromptBox
							context={{
								clientId: values.clientId,
								providers: providers.map((provider) => provider.label),
								sessionTypes: Object.values(sessionTypesByProvider).flat().map((type) => type.label).filter((v, i, a) => a.indexOf(v) === i),
							}}
							onParsed={handleAiParsed}
						/>
					)}

					<FieldArray name="schedules">
						{({ push, remove }) => (
							<>
								{values.schedules.map((block, index) => (
									<ScheduleBlock
										key={block.uid}
										index={index}
										authorizationPeriods={authorizationPeriods}
										providers={providers}
										sessionTypesByProvider={sessionTypesByProvider}
										cancellationsByProvider={cancellationsByProvider}
										canRemove={values.schedules.length > 1}
										onRemove={() => remove(index)}
									/>
								))}
								<button type="button" className="addScheduleButton" onClick={() => push(emptyScheduleBlock())}>
									<AddOutlined fontSize="small" />
									Add Another Schedule for This Client
								</button>
							</>
						)}
					</FieldArray>

					<div className="scheduleModalFooter">
						<CustomButton title="Cancel" buttonType="secondaryGray" sizeType="lg" onClick={onClose} />
						<CustomButton
							title={isSubmitting ? 'Saving...' : 'Save Schedules'}
							buttonType="primary"
							sizeType="lg"
							onClick={submitForm}
							disabled={isSubmitting}
						/>
					</div>
				</>
			)}
		</div>
	);
};

export default ScheduleForm;
